import { useContext, useEffect, useRef, useState } from "react";
import UserContext from "../User/UserContext";
import LoadingCircle from "../utility/LoadingCircle";

const MAX_IMAGES = 5;

const VERDICT_STYLE = {
	PASS: "bg-green-100 text-green-800",
	FAIL: "bg-red-100 text-red-800",
	UNABLE_TO_VERIFY: "bg-amber-100 text-amber-900",
};

// Published GPT-4.1 mini list prices, USD per token.
const MODEL_RATES = {
	"gpt-4.1-mini": { input: 0.4 / 1_000_000, output: 1.6 / 1_000_000 },
};

const estimateCost = (result) => {
	const model = result?.model || "";
	const rates = MODEL_RATES[model] || (model.startsWith("gpt-4.1-mini") ? MODEL_RATES["gpt-4.1-mini"] : null);
	const promptTokens = result?.usage?.prompt_tokens;
	const completionTokens = result?.usage?.completion_tokens;
	if (!rates || promptTokens == null || completionTokens == null) return null;
	return promptTokens * rates.input + completionTokens * rates.output;
};

const formatUsd = (amount) => {
	if (amount < 0.01) return `$${amount.toFixed(4)}`;
	return `$${amount.toFixed(2)}`;
};

/**
 * Temporary demo page to exercise POST /ai (prompt in, model response out).
 * Open at /ai-demo while logged in.
 */
const AiPromptDemo = () => {
	const { api, token } = useContext(UserContext);
	const [instructions, setInstructions] = useState("");
	const [instructionsReady, setInstructionsReady] = useState(false);
	const instructionsEdited = useRef(false);
	const [prompt, setPrompt] = useState("");
	const [fieldValue, setFieldValue] = useState("");
	const [comment, setComment] = useState("");
	const [images, setImages] = useState([]);
	const [previewImage, setPreviewImage] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [imageError, setImageError] = useState("");
	const [result, setResult] = useState(null);
	const tooManyImages = images.length > MAX_IMAGES;
	const imagesRef = useRef(images);
	imagesRef.current = images;

	useEffect(() => {
		return () => {
			imagesRef.current.forEach((image) => URL.revokeObjectURL(image.url));
		};
	}, []);

	useEffect(() => {
		if (!previewImage) return;
		const onKeyDown = (event) => {
			if (event.key === "Escape") setPreviewImage(null);
		};
		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [previewImage]);

	useEffect(() => {
		if (!api || !token) return;
		let cancelled = false;
		const loadInstructions = async () => {
			try {
				const response = await fetch(`${api}/ai/instructions`, {
					headers: {
						Accept: "application/json",
						Authorization: "Bearer " + token,
					},
				});
				const data = await response.json().catch(() => ({}));
				if (cancelled || instructionsEdited.current) return;
				if (response.ok && data.instructions) {
					setInstructions(data.instructions);
				} else {
					setError("Unable to load the saved instructions.");
				}
			} catch (err) {
				if (!cancelled) setError("Unable to load the saved instructions.");
			} finally {
				if (!cancelled) setInstructionsReady(true);
			}
		};
		loadInstructions();
		return () => {
			cancelled = true;
		};
	}, [api, token]);

	const fileDecodesAsImage = (file) =>
		new Promise((resolve) => {
			const url = URL.createObjectURL(file);
			const img = new Image();
			const finish = (ok) => {
				URL.revokeObjectURL(url);
				resolve(ok);
			};
			img.onload = () => finish(img.naturalWidth > 0 && img.naturalHeight > 0);
			img.onerror = () => finish(false);
			img.src = url;
		});

	const addImages = async (fileList) => {
		const candidates = [...fileList].filter((file) => file.type.startsWith("image/"));
		if (candidates.length === 0) {
			setImageError("Choose a photo file.");
			return;
		}
		const rejected = [];
		const next = [];
		for (const file of candidates) {
			const ok = await fileDecodesAsImage(file);
			if (!ok) {
				rejected.push(file.name || "Unnamed photo");
				continue;
			}
			next.push({
				id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
				file,
				url: URL.createObjectURL(file),
			});
		}
		if (next.length > 0) setImages((current) => [...current, ...next]);
		if (rejected.length > 0) {
			const names = rejected.join(", ");
			setImageError(
				rejected.length === 1
					? `Unable to load ${names}, the file may be corrupt or invalid.`
					: `Unable to load ${names}, the files may be corrupt or invalid.`
			);
			return;
		}
		setImageError("");
	};

	const removeImage = (id) => {
		setPreviewImage((current) => (current?.id === id ? null : current));
		setImages((current) => {
			const target = current.find((image) => image.id === id);
			if (target) URL.revokeObjectURL(target.url);
			return current.filter((image) => image.id !== id);
		});
	};

	const sendPrompt = async (event) => {
		event.preventDefault();
		if (!prompt.trim() || loading || tooManyImages) return;
		if (!api || !token) {
			setError("Not signed in or API is unavailable.");
			return;
		}

		setLoading(true);
		setError("");
		setResult(null);

		try {
			const headers = {
				Accept: "application/json",
				Authorization: "Bearer " + token,
			};
			let body;
			if (images.length === 0) {
				headers["Content-Type"] = "application/json";
				body = JSON.stringify({
					prompt: prompt.trim(),
					instructions: instructions.trim(),
					fieldValue: fieldValue.trim(),
					comment: comment.trim(),
				});
			} else {
				body = new FormData();
				body.append("prompt", prompt.trim());
				body.append("instructions", instructions.trim());
				body.append("fieldValue", fieldValue.trim());
				body.append("comment", comment.trim());
				images.forEach((image) => body.append("images", image.file, image.file.name));
			}
			const response = await fetch(`${api}/ai`, {
				method: "POST",
				headers,
				body,
			});
			const data = await response.json().catch(() => ({}));
			if (!response.ok) {
				throw new Error(data.msg || data.error || `Request failed (${response.status})`);
			}
			setResult(data);
		} catch (err) {
			setError(err.message || "Request failed");
		} finally {
			setLoading(false);
		}
	};

	if (!instructionsReady) {
		return (
			<div className="flex justify-center items-center h-[60vh]">
				<LoadingCircle />
			</div>
		);
	}

	return (
		<div className="max-w-6xl mx-auto p-4 md:p-6">
			<div className="mb-4">
				<p className="text-2xl font-bold">AI prompt demo</p>
				<p className="text-sm text-stone-600 mt-1">
					This page lets you try a photo check. Write what to look for, add photos if you have them, then send. Please wait a couple of seconds between tries.
				</p>
			</div>

			<form onSubmit={sendPrompt} className="flex flex-col gap-4">
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
				<div className="flex flex-col gap-3 rounded-xl border border-stone-300 bg-stone-100 p-4">
				<div>
					<p className="text-xs font-semibold uppercase tracking-wide text-stone-500">Check setup</p>
					<p className="mt-1 text-sm text-stone-700">How the photo check should behave for this try.</p>
				</div>
				<label className="text-sm font-semibold" htmlFor="ai-demo-instructions">
					Instructions
				</label>
				<p className="text-xs text-stone-500 -mt-2">
					These are the saved instructions for the photo check. A refresh loads the latest version. You can change the text here to try something else for this send only.
				</p>
				<textarea
					id="ai-demo-instructions"
					value={instructions}
					onChange={(e) => {
						instructionsEdited.current = true;
						setInstructions(e.target.value);
					}}
					rows={8}
					maxLength={8000}
					placeholder="Saved instructions will appear here."
					className="w-full h-48 border border-stone-300 rounded-lg p-3 text-sm bg-white resize-none"
					disabled={loading}
				/>
				<p className="text-xs text-stone-500 text-right">{instructions.length}/8000</p>
				<label className="text-sm font-semibold" htmlFor="ai-demo-prompt">
					Prompt
				</label>
				<p className="text-xs text-stone-500 -mt-2">
					Mention the field value or the comment here when you want the photo check to look for that text in the photo. If this prompt does not mention them, those boxes are ignored.
				</p>
				<textarea
					id="ai-demo-prompt"
					value={prompt}
					onChange={(e) => setPrompt(e.target.value)}
					rows={5}
					maxLength={499}
					placeholder="Ask something short to verify the route…"
					className="w-full border border-stone-300 rounded-lg p-3 text-sm bg-white"
					disabled={loading}
				/>
				<p className="text-xs text-stone-500 text-right">{prompt.length}/499</p>
				</div>
				<div className="flex flex-col gap-3 rounded-xl border border-blue-300 bg-blue-50 p-4">
				<div>
					<p className="text-xs font-semibold uppercase tracking-wide text-blue-800">Sample field</p>
					<p className="mt-1 text-sm text-blue-950">What someone filled in, plus the photos to check.</p>
				</div>
				<label className="text-sm font-semibold" htmlFor="ai-demo-field-value">
					Field value
				</label>
				<p className="text-xs text-stone-500 -mt-2">
					Optional. Sent with the check, and used only when the prompt asks to confirm this text is visible in the photo. Otherwise it is ignored. A refresh clears it.
				</p>
				<textarea
					id="ai-demo-field-value"
					value={fieldValue}
					onChange={(e) => setFieldValue(e.target.value)}
					rows={3}
					maxLength={2000}
					placeholder="The value recorded on the field, if any"
					className="w-full border border-blue-200 rounded-lg p-3 text-sm bg-white"
					disabled={loading}
				/>
				<p className="text-xs text-stone-500 text-right">{fieldValue.length}/2000</p>
				<label className="text-sm font-semibold" htmlFor="ai-demo-comment">
					Comment
				</label>
				<p className="text-xs text-stone-500 -mt-2">
					Optional. Sent with the check, and used only when the prompt asks to confirm this text is visible in the photo. Otherwise it is ignored. A refresh clears it.
				</p>
				<textarea
					id="ai-demo-comment"
					value={comment}
					onChange={(e) => setComment(e.target.value)}
					rows={3}
					maxLength={2000}
					placeholder="The comment recorded on the field, if any"
					className="w-full border border-blue-200 rounded-lg p-3 text-sm bg-white"
					disabled={loading}
				/>
				<p className="text-xs text-stone-500 text-right">{comment.length}/2000</p>
				<div>
					<label className="text-sm font-semibold" htmlFor="ai-demo-images">
						Images
					</label>
					<p className="text-xs text-stone-500 mt-1">
						Add up to {MAX_IMAGES}. If you add more, delete extras before sending.
					</p>
					<input
						id="ai-demo-images"
						type="file"
						accept="image/*"
						multiple
						disabled={loading}
						className="mt-2 block w-full text-sm"
						onChange={(event) => {
							addImages(event.target.files || []);
							event.target.value = "";
						}}
					/>
					<p className={`mt-2 text-xs ${tooManyImages ? "text-red-700 font-medium" : "text-stone-500"}`}>
						{images.length} selected
						{tooManyImages ? ` — delete ${images.length - MAX_IMAGES} before you can send` : ` / ${MAX_IMAGES}`}
					</p>
					{imageError ? (
						<div className="mt-2 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800">{imageError}</div>
					) : null}
					{images.length > 0 ? (
						<ul className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
							{images.map((image) => (
								<li key={image.id} className="border border-stone-200 rounded-lg overflow-hidden bg-white">
									<button
										type="button"
										onClick={() => setPreviewImage(image)}
										className="block w-full"
										aria-label={`View ${image.file.name || "photo"} larger`}>
										<img
											src={image.url}
											alt=""
											className="w-full h-28 object-cover bg-stone-100"
											onError={() => {
												removeImage(image.id);
												setImageError(`Unable to load ${image.file.name || "that photo"}, the file may be corrupt or invalid.`);
											}}
										/>
									</button>
									<div className="flex items-center justify-between gap-2 p-2">
										<p className="min-w-0 truncate text-xs text-stone-600">{image.file.name}</p>
										<button
											type="button"
											disabled={loading}
											onClick={() => removeImage(image.id)}
											className="shrink-0 text-xs px-2 py-1 rounded bg-stone-100 hover:bg-stone-200">
											Delete
										</button>
									</div>
								</li>
							))}
						</ul>
					) : null}
				</div>
				</div>
				</div>
				<div className="flex items-center justify-end gap-3">
					<button
						type="submit"
						disabled={loading || !prompt.trim() || tooManyImages}
						className="px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed">
						{loading ? "Sending…" : "Send"}
					</button>
				</div>
			</form>

			{loading ? (
				<div className="mt-6 flex flex-col items-center gap-2">
					<LoadingCircle />
					<p className="text-sm text-stone-600">Waiting for AI response…</p>
				</div>
			) : null}

			{error ? (
				<div className="mt-6 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800">
					<p className="font-semibold">Error</p>
					<p className="mt-1 whitespace-pre-wrap">{error}</p>
				</div>
			) : null}

			{result ? (
				<div className="mt-6 flex flex-col gap-3">
					{(result.inspection?.results || []).map((item, index) => (
						<div key={`${item.imageIndex}-${index}`} className="rounded-lg border border-stone-200 bg-white p-4">
							<div className="flex flex-wrap items-center gap-2">
								<p className="text-sm font-semibold text-stone-800">
									{item.imageIndex > 0 ? `Image ${item.imageIndex}` : "No image"}
								</p>
								<span className={`rounded px-2 py-0.5 text-xs font-semibold ${VERDICT_STYLE[item.verdict] || "bg-stone-100 text-stone-700"}`}>
									{item.verdict || "—"}
								</span>
							</div>
							{item.explanation ? <p className="mt-2 whitespace-pre-wrap text-sm text-stone-800">{item.explanation}</p> : null}
							{item.nextSteps ? (
								<p className="mt-2 whitespace-pre-wrap text-sm text-stone-700">
									<span className="font-semibold">Next steps: </span>
									{item.nextSteps}
								</p>
							) : null}
						</div>
					))}
					{!result.inspection?.results ? (
						<div className="rounded-lg border border-stone-200 bg-white p-4">
							<p className="text-xs uppercase tracking-wide text-stone-500 font-semibold">Response</p>
							<p className="mt-2 whitespace-pre-wrap text-sm text-stone-900">{result.response || "(empty)"}</p>
						</div>
					) : null}
					<div className="rounded-lg border border-stone-200 bg-stone-50 p-4 text-sm text-stone-700">
						<p>
							<span className="font-semibold">Model:</span> {result.model || "—"}
						</p>
						<p className="mt-1">
							<span className="font-semibold">User:</span> {result.username || "—"}
						</p>
						<p className="mt-1">
							<span className="font-semibold">Images sent:</span> {result.imageCount ?? 0}
						</p>
						<p className="mt-1">
							<span className="font-semibold">Tokens:</span> prompt {result.usage?.prompt_tokens ?? "—"} · completion{" "}
							{result.usage?.completion_tokens ?? "—"} · total {result.usage?.total_tokens ?? "—"}
						</p>
						<p className="mt-1">
							<span className="font-semibold">Estimated cost:</span>{" "}
							{estimateCost(result) == null ? "—" : formatUsd(estimateCost(result))}
							<span className="text-stone-500"> (GPT-4.1 mini list price: $0.40 / 1M input, $1.60 / 1M output)</span>
						</p>
					</div>
				</div>
			) : null}
			<p className="mt-6 text-sm text-stone-500">
				Sends your text to <code className="bg-stone-100 px-1 rounded">POST /ai</code> and shows the model reply. Rate limit: 1 request / 2 seconds.
			</p>
			{previewImage ? (
				<div className="fixed inset-0 z-[400] bg-black/80" onClick={() => setPreviewImage(null)}>
					<button
						type="button"
						onClick={() => setPreviewImage(null)}
						className="absolute right-4 top-4 z-10 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-stone-900 shadow-lg">
						Close
					</button>
					<div className="flex h-full w-full items-center justify-center p-4 pt-16">
						<img
							src={previewImage.url}
							alt=""
							className="max-h-full max-w-full object-contain"
							onClick={(event) => event.stopPropagation()}
						/>
					</div>
				</div>
			) : null}
		</div>
	);
};

export default AiPromptDemo;
